import { createContext, useContext, useState, useCallback } from 'react'
import profile from '../data/profiles/activeProfile'

const SYSTEM_PROMPT = profile.ai.systemPrompt

const INITIAL_MESSAGE = {
  role: 'ai',
  text: profile.ai.initialMessage,
}

const ChatContext = createContext(null)

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([INITIAL_MESSAGE])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const openChat = useCallback(() => setIsOpen(true), [])
  const closeChat = useCallback(() => setIsOpen(false), [])
  const toggleChat = useCallback(() => setIsOpen((o) => !o), [])

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || isLoading) return

      const userMsg = { role: 'user', text }
      const updatedMessages = [...messages, userMsg]
      setMessages(updatedMessages)
      setIsLoading(true)

      // Convert to Anthropic format, skip initial welcome, cap at last 6 messages
      const apiMessages = updatedMessages
        .filter((msg, i) => !(i === 0 && msg.role === 'ai'))
        .map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.text,
        }))
        .slice(-6)

      try {
        let aiText

        if (process.env.NODE_ENV === 'development') {
          // Local dev: call Anthropic directly from browser (CORS is supported)
          const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY
          if (!apiKey) throw new Error('REACT_APP_ANTHROPIC_API_KEY is not set in .env')

          const res = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': apiKey,
              'anthropic-version': '2023-06-01',
              'anthropic-dangerous-direct-browser-access': 'true',
            },
            body: JSON.stringify({
              model: 'claude-haiku-4-5-20251001',
              max_tokens: 512,
              system: SYSTEM_PROMPT,
              messages: apiMessages,
            }),
          })
          const data = await res.json()
          if (!res.ok) throw new Error(data.error?.message || 'Anthropic API error')
          aiText = data.content?.[0]?.text
        } else {
          // Production: go through Netlify function (key stays server-side)
          const res = await fetch('/.netlify/functions/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages: apiMessages, system: SYSTEM_PROMPT }),
          })
          const data = await res.json()
          if (!res.ok) throw new Error(data.error || 'Request failed')
          aiText = data.text
        }

        setMessages((prev) => [
          ...prev,
          { role: 'ai', text: aiText || "Sorry, I couldn't generate a response. Please try again." },
        ])
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: 'ai', text: `Something went wrong: ${err.message}` },
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [messages, isLoading]
  )

  const clearMessages = useCallback(() => {
    setMessages([{ role: 'ai', text: profile.ai.initialMessage }])
  }, [])

  return (
    <ChatContext.Provider
      value={{ messages, isLoading, sendMessage, clearMessages, isOpen, openChat, closeChat, toggleChat }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export const useChat = () => useContext(ChatContext)
